import React from 'react';
import PropTypes from 'prop-types';
import { Advert, refreshSlot, destroySlot } from '@zillow/mst-display-ad';
import { DeferredRender, DeferredRenderModes } from '@zillow/react-deferred-hydration';
import isEqual from 'react-fast-compare';
import { useSelector, useDispatch } from 'react-redux';
import { adSlotRequested, AD_SLOTS } from '@zillow/ad-state-machine';

import {
    deviceType,
    onSlotRenderEnded,
    onMessage,
    getSlotSize,
    onSlotOnload,
    shouldAdHavePrebidState,
    updateVersValue,
} from '../util/common';
import { NAV_AD_TYPE } from '../constants';
import { useWindowEvent } from '../../../hooks/useWindowEvent';
import { useEffectOnce } from '../../../hooks/useEffectOnce';

export const SearchStateAd = ({
    adSlot,
    targetDiv,
    onSlotRenderEndedSlot,
    iabEnabled,
    adGptScriptLoaded,
    adPrebidScriptFailed,
    adPrebidScriptLoaded,
    isTall,
    hasBeenVisible,
}) => {
    const adType = adSlot === AD_SLOTS.DISPLAY ? adSlot : NAV_AD_TYPE;
    const dispatch = useDispatch();
    const { adState, isMobile, isTablet, adTargets, isRestricted } = useSelector(
        state => ({
            adState: state.adSlots && state.adSlots[adSlot],
            isMobile: state.isMobile,
            isTablet: state.isTablet,
            adTargets: state.adsConfig.targets,
            isRestricted: state.isAdRestricted,
        }),
        (previous, current) =>
            isEqual(previous.adState, current.adState) &&
            isEqual(previous.isMobile, current.isMobile) &&
            isEqual(previous.isTablet, current.isTablet)
    );

    useEffectOnce(() => () => {
        // if we have the ad hidden, then we need to unhide it on unmount before we switch
        // back to the legacy ad
        if (typeof onSlotRenderEndedSlot === 'function') {
            onSlotRenderEndedSlot({ hideAd: false });
        }

        destroySlot(targetDiv);
    });

    useWindowEvent({
        eventName: 'message',
        callback: onMessage,
        args: [adType, onSlotRenderEndedSlot],
    });

    // eslint-disable-next-line no-unused-vars
    const { vers, treatments, refreshAd, showAd, destroyAd, path } = adState ?? {};

    const isPrebidEnabled = adSlot === AD_SLOTS.DISPLAY || iabEnabled;

    const device = deviceType({ isTablet, isMobile });
    const adSizes = getSlotSize(adType, iabEnabled);
    const pageType = adTargets?.listtp;
    const shouldHavePrebid = shouldAdHavePrebidState({
        isRestricted,
        adPrebidScriptFailed: adPrebidScriptFailed && !adPrebidScriptLoaded,
        isPrebidEnabled,
    });
    let modifiedTargets = {
        ...adTargets,
        vers,
    };
    modifiedTargets = updateVersValue(modifiedTargets, 'z_srp_web_state_machine-test');
    if (adSlot !== AD_SLOTS.DISPLAY) {
        modifiedTargets.page_type = isTall ? 'tall' : 'short';
    }

    const inventory = {
        targets: modifiedTargets,
        path,
        isRestricted,
        device,
        adSizes,
        targetDiv,
        pageType,
        collapseAfterRequest: true,
        collapseBeforeRequest: false,
        isPrebid: shouldHavePrebid,
    };

    if (!showAd) {
        if (typeof onSlotRenderEndedSlot === 'function') {
            onSlotRenderEndedSlot({ hideAd: true });
        }
        if (adState?.requestTime === undefined) {
            return null;
        }
    } else {
        if (typeof onSlotRenderEndedSlot === 'function') {
            onSlotRenderEndedSlot({ hideAd: false });
        }
        if (adState?.requestTime === undefined) {
            dispatch(adSlotRequested(adSlot));
        }
    }

    // for handling lazy loaded ads
    if (!hasBeenVisible) {
        return null;
    }

    if (destroyAd && showAd) {
        destroySlot(targetDiv);
        dispatch(adSlotRequested(adSlot));
    }

    if (refreshAd && showAd && !destroyAd) {
        refreshSlot(targetDiv, modifiedTargets, { isPrebid: shouldHavePrebid });
        dispatch(adSlotRequested(adSlot));
    }

    return (
        <DeferredRender
            mode={DeferredRenderModes.RENDER_CLIENT_SIDE_ON_IMMEDIATE}
            data-testid="mst-ad-display"
        >
            <Advert
                adInventory={inventory}
                targetDiv={targetDiv}
                targets={modifiedTargets}
                variant="zsrp"
                prebidonload={adPrebidScriptLoaded}
                gptonload={adGptScriptLoaded}
                isSRA
                blockGptLib
                blockPrebidLib
                isPrebidAllowed={shouldHavePrebid}
                prebidPageType={shouldHavePrebid ? 'zillow_srp' : null}
                eventList={['slotRenderEnded', 'impressionViewable', 'slotOnload']}
                onSlotRenderEnded={e => onSlotRenderEnded(e, adType, onSlotRenderEndedSlot, path)}
                onSlotOnload={e => onSlotOnload(e)}
            />
        </DeferredRender>
    );
};

SearchStateAd.propTypes = {
    adSlot: PropTypes.string,
    targetDiv: PropTypes.string.isRequired,
    onSlotRenderEndedSlot: PropTypes.func,
    iabEnabled: PropTypes.bool,
    adGptScriptLoaded: PropTypes.bool,
    adPrebidScriptFailed: PropTypes.bool,
    adPrebidScriptLoaded: PropTypes.bool,
    isTall: PropTypes.bool,
};
